// useStorageBridge.ts
// Uses URL hash (#) to pass data — hash is never sent to the server.

import { useCallback, useRef } from "react";

const BRIDGE_ORIGIN = import.meta.env.VITE_APP_FORM_SITE;
const BRIDGE_PATH = "/contactForm/bridge";
const POPUP_NAME = "storage_bridge";
const POPUP_FEATURES = "width=1,height=1,left=-9999,top=-9999";
const TIMEOUT_MS = 10000;

export type StorageData = Record<string, string | null>;

export type BridgeError =
  | { code: "POPUP_BLOCKED" }
  | { code: "TIMEOUT" }
  | { code: "BRIDGE_ERROR"; message: string }
  | { code: "INVALID_KEYS" }
  | { code: "SERIALIZE_ERROR"; message: string };

export class StorageBridgeError extends Error {
  constructor(public readonly error: BridgeError) {
    super(bridgeErrorMessage(error));
    this.name = "StorageBridgeError";
  }
}

function bridgeErrorMessage(e: BridgeError): string {
  switch (e.code) {
    case "POPUP_BLOCKED":
      return "Popup was blocked. Allow popups for this site.";
    case "TIMEOUT":
      return `Bridge did not respond within ${TIMEOUT_MS}ms.`;
    case "BRIDGE_ERROR":
      return `Bridge reported error: ${e.message}`;
    case "INVALID_KEYS":
      return "Keys must be a non-empty array of strings.";
    case "SERIALIZE_ERROR":
      return `Failed to serialize data: ${e.message}`;
  }
}

// Encode to base64url so special chars are safe in the hash, data never reaches server
function encodeHash(data: unknown): string {
  return btoa(unescape(encodeURIComponent(JSON.stringify(data))))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export { encodeHash };

export function decodeHash(raw: string): unknown {
  const base64 = raw.replace(/^#/, "").replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(decodeURIComponent(escape(atob(base64))));
}

// ─────────────────────────────────────────────

export function useStorageBridge() {
  const popupRef = useRef<Window | null>(null);

  /**
   * STEP 1 — call this synchronously inside the click handler (no await before it).
   * Opens the popup immediately to satisfy iOS Safari's gesture requirement.
   * The popup loads a bare /bridge page with no hash yet.
   *
   * Returns the popup reference to be passed to fetchFromStorage / writeToStorage.
   */
  const openPopup = useCallback((): Window => {
    // Reuse if still open
    if (popupRef.current && !popupRef.current.closed) {
      return popupRef.current;
    }

    // Open with no hash yet — just gets the page loading, preserves gesture context
    const popup = window.open(
      `${BRIDGE_ORIGIN}${BRIDGE_PATH}`,
      POPUP_NAME,
      POPUP_FEATURES,
    );

    if (!popup) {
      throw new StorageBridgeError({ code: "POPUP_BLOCKED" });
    }

    popupRef.current = popup;
    return popup;
  }, []);

  const waitForMessage = useCallback(<T>(expectedType: string): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        window.removeEventListener("message", handler);
        reject(new StorageBridgeError({ code: "TIMEOUT" }));
      }, TIMEOUT_MS);

      function handler(event: MessageEvent) {
        console.log("Got message from popup", event, BRIDGE_ORIGIN);

        if (event.origin !== BRIDGE_ORIGIN) return;

        clearTimeout(timer);
        window.removeEventListener("message", handler);

        if (event.data?.type === "BRIDGE_ERROR") {
          reject(
            new StorageBridgeError({
              code: "BRIDGE_ERROR",
              message: event.data.message ?? "Unknown error",
            }),
          );
          return;
        }

        if (event.data?.type === expectedType) {
          resolve(event.data as T);
          return;
        }

        reject(
          new StorageBridgeError({
            code: "BRIDGE_ERROR",
            message: `Unexpected message type: ${event.data?.type}`,
          }),
        );
      }

      window.addEventListener("message", handler);
    });
  }, []);

  /**
   * STEP 2 — read keys from second.pl first-party localStorage.
   *
   * Usage:
   *   function handleClick() {                    // ← user gesture
   *     const popup = openPopup();                // ← sync, no await
   *     fetchFromStorage(popup, ["key"]).then(…); // ← async after
   *   }
   */
  const fetchFromStorage = useCallback(
    async (popup: Window, keys: string[]): Promise<StorageData> => {
      if (
        !Array.isArray(keys) ||
        keys.length === 0 ||
        keys.some((k) => typeof k !== "string")
      ) {
        popup.close();
        throw new StorageBridgeError({ code: "INVALID_KEYS" });
      }

      let hash: string;
      try {
        hash = encodeHash({ action: "read", keys });
      } catch (e) {
        popup.close();
        throw new StorageBridgeError({
          code: "SERIALIZE_ERROR",
          message: String(e),
        });
      }

      // Navigate the already-open popup to the hash
      popup.location.href = `${BRIDGE_ORIGIN}${BRIDGE_PATH}#${hash}`;

      const msg = await waitForMessage<{ type: string; payload: StorageData }>(
        "BRIDGE_RESPONSE",
      );
      return msg.payload;
    },
    [waitForMessage],
  );

  /**
   * STEP 2 — write data to second.pl first-party localStorage.
   * Pass null as a value to remove a key.
   *
   * Usage:
   *   function handleClick() {                         // ← user gesture
   *     const popup = openPopup();                     // ← sync, no await
   *     writeToStorage(popup, { key: "val" }).then(…); // ← async after
   *   }
   */
  const writeToStorage = useCallback(
    async (popup: Window, data: StorageData): Promise<void> => {
      if (!data || typeof data !== "object" || Array.isArray(data)) {
        popup.close();
        throw new StorageBridgeError({ code: "INVALID_KEYS" });
      }

      let hash: string;
      try {
        hash = encodeHash({ action: "write", payload: data });
      } catch (e) {
        popup.close();
        throw new StorageBridgeError({
          code: "SERIALIZE_ERROR",
          message: String(e),
        });
      }

      popup.location.href = `${BRIDGE_ORIGIN}${BRIDGE_PATH}#${hash}`;

      await waitForMessage<{ type: string }>("BRIDGE_WRITE_ACK");
    },
    [waitForMessage],
  );

  return { openPopup, fetchFromStorage, writeToStorage };
}
